<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  url,
  requiredIf,
  requiredUnless,
} from '@vuelidate/validators';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Button from 'next/button/Button.vue';

const emit = defineEmits(['submit']);

const { t } = useI18n();

const state = reactive({
  display_name: '',
  sp_metadata_url: '',
  sp_metadata: '',
  useUrl: true,
});

const rules = {
  display_name: { required, minLength: minLength(1) },
  sp_metadata_url: { required: requiredIf(() => state.useUrl), url },
  sp_metadata: { required: requiredUnless(() => state.useUrl) },
};

const v$ = useVuelidate(rules, state);

const displayNameError = computed(() =>
  v$.value.display_name.$error
    ? t('WGPT_SAML.PROVIDERS.CREATE.FORM.DISPLAY_NAME.ERROR')
    : ''
);

const spMetadataUrlError = computed(() =>
  v$.value.sp_metadata_url.$error
    ? t('WGPT_SAML.PROVIDERS.CREATE.FORM.SP_METADATA_URL.ERROR')
    : ''
);

const spMetadataError = computed(() =>
  v$.value.sp_metadata.$error
    ? t('WGPT_SAML.PROVIDERS.CREATE.FORM.SP_METADATA.ERROR')
    : ''
);

const onSubmit = () => {
  emit('submit');
};

defineExpose({ state, v$ });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
    <Button class="hidden" type="button" />

    <Input
      v-model="state.display_name"
      :label="t('WGPT_SAML.PROVIDERS.CREATE.FORM.DISPLAY_NAME.LABEL')"
      :message="displayNameError"
      :message-type="displayNameError ? 'error' : 'info'"
      custom-input-class="!h-10"
      @input="v$.display_name.$touch"
    />

    <div>
      <label>
        {{ t('WGPT_SAML.PROVIDERS.CREATE.FORM.METADATA.LABEL') }}
      </label>
      <div class="flex gap-4">
        <label class="inline-flex items-center gap-1.5">
          <input
            v-model="state.useUrl"
            type="radio"
            :value="true"
            class="h-4 w-4 border-n-slate-6 text-n-brand focus:ring-n-brand focus:ring-offset-0"
          />
          <span>{{ t('WGPT_SAML.PROVIDERS.CREATE.FORM.USE_URL.LABEL') }}</span>
        </label>
        <label class="inline-flex items-center gap-1.5">
          <input
            v-model="state.useUrl"
            type="radio"
            :value="false"
            class="h-4 w-4 border-n-slate-6 text-n-brand focus:ring-n-brand focus:ring-offset-0"
          />
          <span>{{ t('WGPT_SAML.PROVIDERS.CREATE.FORM.USE_XML.LABEL') }}</span>
        </label>
      </div>
    </div>

    <template v-if="state.useUrl">
      <Input
        v-model="state.sp_metadata_url"
        :message="spMetadataUrlError"
        :message-type="spMetadataUrlError ? 'error' : 'info'"
        :placeholder="
          t('WGPT_SAML.PROVIDERS.CREATE.FORM.SP_METADATA_URL.PLACEHOLDER')
        "
        type="url"
        custom-input-class="!h-10"
        @input="v$.sp_metadata_url.$touch"
      />
    </template>
    <template v-else>
      <TextArea
        v-model="state.sp_metadata"
        :message="spMetadataError"
        :message-type="spMetadataError ? 'error' : 'info'"
        :placeholder="
          t('WGPT_SAML.PROVIDERS.CREATE.FORM.SP_METADATA.PLACEHOLDER')
        "
        custom-text-area-class="!outline !outline-0"
        @input="v$.sp_metadata.$touch"
      />
    </template>
  </form>
</template>
